<template>
    <div class="px-4 py-3 xl:px-8">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    {{ t("exams.management") }}
                </h1>
                <router-link
                    v-if="canCreateExam"
                    to="/exams/create"
                    class="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    {{ t("exams.addExam") }}
                </router-link>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-3 mb-4">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            {{ t("common.search") }}
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            :placeholder="t('exams.searchPlaceholder')"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Class Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_id"
                            :options="classOptions"
                            :label="t('common.class')"
                            :placeholder="t('exams.allClasses')"
                            :option-label="getClassLabel"
                            option-value="id"
                            @update:model-value="fetchExams"
                        />
                    </div>

                    <!-- Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.subject_id"
                            :options="subjectOptions"
                            :label="t('exams.subject')"
                            :placeholder="t('exams.allSubjects')"
                            :option-label="getSubjectLabel"
                            option-value="id"
                            @update:model-value="fetchExams"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.status"
                        @change="fetchExams"
                        all-label="All Status"
                        :options="[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                            { value: 'draft', label: 'Draft' },
                            { value: 'published', label: 'Published' },
                        ]"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="examStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        {{ t("exams.resetFilters") }}
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="examStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="examStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ examStore.error }}
                        </p>
                        <button
                            @click="examStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!examStore.loading && !examStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.examName") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.subject") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.class") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("common.name") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.startTime") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.endTime") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("exams.passingScore") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("common.status") }}
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    {{ t("common.actions") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="exam in examStore.exams"
                                :key="exam.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ exam.title }}
                                    </div>
                                    <div
                                        v-if="exam.description"
                                        class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                    >
                                        {{ exam.description }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        exam.class_subject?.subject?.name || "-"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ exam.class_subject?.class?.grade_level }}
                                    -
                                    {{ exam.class_subject?.class?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ exam.teacher?.user?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDateTime(exam.start_time) }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDateTime(exam.end_time) }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ exam.passing_score || "-" }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="
                                            getStatusBadgeClass(exam.status)
                                        "
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    >
                                        {{ t(`exams.${exam.status}`) }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <div class="flex items-center space-x-2">
                                        <!-- Status Update Dropdown -->
                                        <select
                                            v-model="exam.status"
                                            @change="
                                                updateExamStatus(
                                                    exam.id,
                                                    $event,
                                                )
                                            "
                                            :disabled="
                                                updatingStatus === exam.id
                                            "
                                            class="text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white px-2 py-1"
                                        >
                                            <option
                                                v-if="
                                                    updatingStatus === exam.id
                                                "
                                                value=""
                                                disabled
                                            >
                                                {{ t("exams.updating") }}
                                            </option>
                                            <option value="draft">
                                                {{ t("exams.draft") }}
                                            </option>
                                            <option value="scheduled">
                                                {{ t("exams.scheduled") }}
                                            </option>
                                            <option value="ongoing">
                                                {{ t("exams.ongoing") }}
                                            </option>
                                            <option value="closed">
                                                {{ t("exams.completed") }}
                                            </option>
                                            <option value="archived">
                                                {{ t("exams.archived") }}
                                            </option>
                                        </select>

                                        <!-- Edit Button -->
                                        <router-link
                                            :to="`/exams/${exam.id}/edit`"
                                            title="Edit Exam"
                                            class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                        >
                                            <Pencil class="w-4 h-4" />
                                        </router-link>

                                        <!-- View Attempts/Grade Button -->
                                        <router-link
                                            :to="getResultsRoute(exam)"
                                            title="View Results"
                                            class="inline-flex items-center justify-center p-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors mr-2"
                                        >
                                            <BarChart3 class="w-4 h-4" />
                                        </router-link>

                                        <!-- Delete Button -->
                                        <button
                                            v-if="canDeleteExam"
                                            @click="confirmDelete(exam)"
                                            title="Delete Exam"
                                            class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash2 class="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="examStore.exams.length === 0"
                    class="text-center py-12"
                >
                    <FileText class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        {{ t("exams.noExamsFound") }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {{ t("exams.getStarted") }}
                    </p>
                    <div v-if="canCreateExam" class="mt-6">
                        <router-link
                            to="/exams/create"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            {{ t("exams.addExam") }}
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (examStore.meta?.total || 0) >
                    (examStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-3 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-4"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="examStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        {{ t("pagination.prev") }}
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            examStore.meta?.current_page ===
                            examStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        {{ t("pagination.next") }}
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            {{ t("pagination.showing") }}
                            <span class="font-medium">{{
                                examStore.meta?.from ?? 0
                            }}</span>
                            {{ t("pagination.to") }}
                            <span class="font-medium">{{
                                examStore.meta?.to ?? 0
                            }}</span>
                            {{ t("pagination.of") }}
                            <span class="font-medium">{{
                                examStore.meta?.total ?? 0
                            }}</span>
                            {{ t("common.results") }}
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="examStore.meta?.current_page === 1"
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                {{ t("pagination.prev") }}
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === examStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    examStore.meta?.current_page ===
                                    examStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                {{ t("pagination.next") }}
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
        @click.self="showDeleteModal = false"
    >
        <div
            class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800"
        >
            <div class="mt-3 text-center">
                <h3
                    class="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                >
                    {{ t("messages.confirmDelete") }}
                </h3>
                <div class="mt-2 px-7 py-3">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{ deleteModalMessage }}
                    </p>
                </div>
                <div class="items-center px-4 py-3">
                    <button
                        @click="showDeleteModal = false"
                        class="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200"
                    >
                        {{ t("common.cancel") }}
                    </button>
                    <button
                        @click="deleteExam"
                        class="ml-3 px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                    >
                        {{ t("common.delete") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useExamStore } from "@/stores/examStore";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/authStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import classSchoollApi from "@/api/classSchooll";
import subjectApi from "@/api/subject";
import examApi from "@/api/exam";
import type { Exam } from "@/api/exam";
import type { ClassSchooll } from "@/api/classSchooll";
import type { Subject } from "@/api/subject";
import { RotateCcw, Pencil, BarChart3, Trash2, FileText } from "@lucide/vue";

const examStore = useExamStore();
const { t } = useI18n();
const authStore = useAuthStore();

// Role-based access control
const canCreateExam = computed(
    () =>
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator"),
);

const canDeleteExam = computed(
    () =>
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator"),
);

// State
const searchQuery = ref("");
const filters = ref({
    class_id: "",
    subject_id: "",
    status: "",
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedExam = ref<Exam | null>(null);
const isEditing = ref(false);
const examToDelete = ref<Exam | null>(null);
const updatingStatus = ref<string | null>(null);
// Types for options with "Select All" option
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SubjectOption = Subject | { id: ""; name: string; code: string };

const classOptions = ref<ClassOption[]>([]);
const subjectOptions = ref<SubjectOption[]>([]);

// Delete modal properties
const deleteModalTitle = computed(() => t("messages.confirmDelete"));
const deleteModalMessage = computed(() => t("messages.deleteConfirmExam"));

// Computed
const visiblePages = computed(() => {
    const current = examStore.meta?.current_page || 1;
    const last = examStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.class_id !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.status !== "" ||
        examStore.page !== 1 ||
        examStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = computed(() => [
    { value: "", label: t("exams.allStatuses") },
    { value: "draft", label: t("exams.draft") },
    { value: "scheduled", label: t("exams.scheduled") },
    { value: "ongoing", label: t("exams.ongoing") },
    { value: "closed", label: t("exams.completed") },
    { value: "archived", label: t("exams.archived") },
]);

// Methods
const handlePerPageChange = (value: number) => {
    examStore.perPage = value;
    examStore.page = 1;
    fetchExams();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { class_id: "", subject_id: "", status: "" };
    examStore.page = 1;
    examStore.perPage = 10;
    fetchExams();
};

const fetchExams = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
        status: filters.value.status as
            | "draft"
            | "scheduled"
            | "ongoing"
            | "closed"
            | "archived"
            | undefined,
    };
    await examStore.fetchExams(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [classesResponse, subjectsResponse] = await Promise.all([
            classSchoollApi.getAll({ per_page: 1000 }),
            subjectApi.getAll({ per_page: 1000 }),
        ]);
        // Add "Select All" option as the first element
        classOptions.value = [
            { id: "", grade_level: "", name: t("exams.allClasses") },
            ...(classesResponse.data?.items || []),
        ];
        subjectOptions.value = [
            { id: "", name: t("exams.allSubjects"), code: "" },
            ...(subjectsResponse.data?.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSubjectLabel = (subjectOption: SubjectOption): string => {
    if (subjectOption.id === "") return subjectOption.name;
    return `${subjectOption.name} (${subjectOption.code})`;
};

const debouncedSearch = useDebounce(fetchExams, 300);

const previousPage = () => {
    if ((examStore.meta?.current_page || 1) > 1) {
        examStore.setPage((examStore.meta?.current_page || 1) - 1);
        fetchExams();
    }
};

const nextPage = () => {
    if (
        (examStore.meta?.current_page || 1) < (examStore.meta?.last_page || 1)
    ) {
        examStore.setPage((examStore.meta?.current_page || 1) + 1);
        fetchExams();
    }
};

const goToPage = (page: number) => {
    examStore.setPage(page);
    fetchExams();
};

const formatDateTime = (dateTime: string) => {
    if (!dateTime) return "-";
    return new Date(dateTime).toLocaleString();
};

const getStatusBadgeClass = (status: string) => {
    switch (status) {
        case "draft":
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
        case "scheduled":
            return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
        case "ongoing":
            return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
        case "closed":
            return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
        case "archived":
            return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
        default:
            return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case "draft":
            return t("exams.draft");
        case "scheduled":
            return t("exams.scheduled");
        case "ongoing":
            return t("exams.ongoing");
        case "closed":
            return t("exams.completed");
        case "archived":
            return t("exams.archived");
        default:
            return status;
    }
};

const confirmDelete = (exam: Exam) => {
    selectedExam.value = exam;
    examToDelete.value = exam;
    showDeleteModal.value = true;
};

const updateExamStatus = async (examId: string, event: Event) => {
    const newStatus = (event.target as HTMLSelectElement).value as
        | "draft"
        | "scheduled"
        | "ongoing"
        | "closed"
        | "archived";

    // DEBUG: Log the status update attempt
    console.log("DEBUG: updateExamStatus - Attempting to update status", {
        examId,
        newStatus,
        updateData: { status: newStatus },
    });

    try {
        updatingStatus.value = examId; // Set updating flag

        // DEBUG: Log the API call
        console.log(
            "DEBUG: updateExamStatus - Making API call to examApi.update",
        );

        const response = await examApi.updateStatus(examId, newStatus);

        // DEBUG: Log successful response
        console.log("DEBUG: updateExamStatus - API call successful", {
            response,
            updatedExam: response,
        });

        NotificationService.success(`Exam status changed to ${newStatus}`);
        updatingStatus.value = null; // Clear updating flag

        // Refresh the exams list to show updated status
        await fetchExams();
    } catch (error) {
        // DEBUG: Log the error details
        console.error("DEBUG: updateExamStatus - API call failed", {
            error,
            errorMessage:
                error instanceof Error ? error.message : String(error),
            errorResponse:
                error && typeof error === "object" && "response" in error
                    ? (error as any).response?.data
                    : undefined,
            examId,
            newStatus,
            updateData: { status: newStatus },
        });

        NotificationService.error("Failed to update exam status");
        updatingStatus.value = null;
    }
};

const deleteExam = async () => {
    if (!examToDelete.value) return;

    try {
        await examStore.deleteExam(examToDelete.value.id);
        showNotification("Exam deleted successfully", "success");
        showDeleteModal.value = false;
        examToDelete.value = null;
        selectedExam.value = null;
        await fetchExams(); // Refresh the list
    } catch (error) {
        console.error("Delete error:", error);
        showNotification("Failed to delete exam", "error");
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchExams();
    fetchOptions();
});

// Methods for role-based routing
const getResultsRoute = (exam: Exam) => {
    // Check if current user is a teacher or admin
    if (
        authStore.hasRole("teacher") ||
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator")
    ) {
        return `/exams/${exam.id}/results`;
    } else if (authStore.hasRole("student")) {
        return `/student-exams/${exam.id}/results`;
    } else {
        // Default to teacher route for admin or other roles
        return `/exams/${exam.id}/results`;
    }
};

const getResultsButtonText = (exam: Exam) => {
    if (
        authStore.hasRole("teacher") ||
        authStore.hasRole("administrator") ||
        authStore.hasRole("super-administrator")
    ) {
        return t("exams.results");
    } else if (authStore.hasRole("student")) {
        return t("exams.myResults");
    } else {
        return t("exams.results");
    }
};
</script>
